import { datadogLogs } from "@datadog/browser-logs";
/**
 * Stale-build detection (PD-3751).
 *
 * Every deploy bakes a BUILD_ID (`<UTC yyyyMMddHHmmss>-<sha7>`) into the bundle and into `/version.json`. Tabs that
 * survive a deploy (e.g. restored iOS Chrome tabs) keep running the old JS; comparing the two ids tells us the tab is
 * stale. Only a NEWER deployed build counts: during a rolling restart an old pod may still answer, and that must never
 * flag a fresh tab.
 *
 * `version.json` is fetched with plain `fetch` on purpose, not `@/utils/request`: it is a same-origin static file, and
 * the shared client would attach the Bearer token, try a refresh on 401 and show error toasts — all wrong for a
 * background probe. Every check fails silently: a failed or slow fetch means "no update".
 */
const VERSION_URL = "/version.json";
const LOCAL_BUILD_ID = "local";
const BUILD_ID_PATTERN = /^(\d{14})-/;
export const VERSION_FETCH_TIMEOUT_MS = 3 * 1000;
export const RELOAD_STORAGE_KEY = "pd3751.lastBuildReload";
export const RELOAD_GUARD_MS = 60 * 1000;
let updateAvailable = false;
let reloadInProgress = false;
const subscribers = new Set();
/** Local dev builds have no version.json and never go stale. */
export const isBuildCheckEnabled = () => BUILD_ID !== LOCAL_BUILD_ID;
/**
 * `true` only when `deployed` is strictly newer than `running`. Both ids must start with a 14-digit UTC timestamp;
 * anything else is never treated as newer.
 */
export const isNewerBuild = (deployed, running) => {
    const deployedMatch = BUILD_ID_PATTERN.exec(deployed);
    const runningMatch = BUILD_ID_PATTERN.exec(running);
    if (!deployedMatch || !runningMatch)
        return false;
    // same-length digit strings compare correctly as strings
    return deployedMatch[1] > runningMatch[1];
};
const fetchBuildId = async () => {
    const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
        cache: "no-store",
        credentials: "same-origin",
    });
    if (!response.ok)
        return null;
    const data = await response.json();
    const buildId = data?.buildId;
    return typeof buildId === "string" && buildId.length > 0 ? buildId : null;
};
/**
 * The build id currently deployed, or `null` when it cannot be determined within 3s. Never throws.
 * The timeout uses `Promise.race` because `AbortController` is not guaranteed on the IE11 target.
 */
export const fetchLatestBuildId = async () => {
    let timeoutId;
    const timedOut = new Promise(resolve => {
        timeoutId = setTimeout(() => resolve(null), VERSION_FETCH_TIMEOUT_MS);
    });
    try {
        return await Promise.race([fetchBuildId().catch(() => null), timedOut]);
    }
    catch {
        return null;
    }
    finally {
        clearTimeout(timeoutId);
    }
};
export const isUpdateAvailable = () => updateAvailable;
/** Subscribe to the "a new build is available" event. Returns an unsubscribe function. */
export const subscribeToBuildUpdate = (subscriber) => {
    subscribers.add(subscriber);
    return () => {
        subscribers.delete(subscriber);
    };
};
/**
 * Checks whether a newer build has been deployed. The first detection latches the shared flag, logs it and notifies
 * subscribers (once). Resolves `true` when an update is available. Never throws.
 */
export const checkForNewBuild = async () => {
    if (!isBuildCheckEnabled())
        return false;
    if (updateAvailable)
        return true;
    const deployedBuildId = await fetchLatestBuildId();
    // an overlapping check may have latched the update while this fetch was in flight
    if (!deployedBuildId || !isNewerBuild(deployedBuildId, BUILD_ID))
        return updateAvailable;
    if (updateAvailable)
        return true;
    updateAvailable = true;
    try {
        datadogLogs.logger.info("Stale build detected", { runningBuildId: BUILD_ID, deployedBuildId });
    }
    catch {
        // telemetry must never break detection
    }
    subscribers.forEach(subscriber => {
        try {
            subscriber();
        }
        catch (e) {
            console.error(e);
        }
    });
    return true;
};
const readLastReloadAt = () => {
    try {
        const raw = window.sessionStorage.getItem(RELOAD_STORAGE_KEY);
        return raw ? Number(JSON.parse(raw)?.at) : NaN;
    }
    catch {
        // a corrupt value means no usable record of a previous reload
        return NaN;
    }
};
const logReloadRefused = (reason) => {
    try {
        datadogLogs.logger.warn("Stale build reload refused", { reason, runningBuildId: BUILD_ID });
    }
    catch {
        // telemetry must never break recovery
    }
};
/**
 * Reloads the page unless a reload already ran in the last 60s (tracked in sessionStorage), which breaks reload loops
 * during a rolling restart (e.g. a chunk that stays missing). Returns `true` when a reload was started.
 */
export const reloadOnce = () => {
    const now = Date.now();
    const lastAt = readLastReloadAt();
    if (Number.isFinite(lastAt) && now - lastAt >= 0 && now - lastAt < RELOAD_GUARD_MS) {
        logReloadRefused("reloaded-recently");
        return false;
    }
    try {
        window.sessionStorage.setItem(RELOAD_STORAGE_KEY, JSON.stringify({ at: now }));
    }
    catch {
        // Without a working guard we cannot rule out a loop, so let the user refresh manually.
        logReloadRefused("storage-unavailable");
        return false;
    }
    reloadInProgress = true;
    window.location.reload();
    return true;
};
/** `true` once `reloadOnce` has started a reload in this document. */
export const isReloadInProgress = () => reloadInProgress;
/**
 * `true` for webpack's "Loading chunk <id> failed." rejection (`error.name === "ChunkLoadError"`) and for
 * mini-css-extract-plugin's CSS chunk failure (`error.code === "CSS_CHUNK_LOAD_FAILED"`).
 */
export const isChunkLoadError = (reason) => {
    if (!reason || typeof reason !== "object")
        return false;
    const { name, code } = reason;
    return name === "ChunkLoadError" || code === "CSS_CHUNK_LOAD_FAILED";
};
/** Test-only: reset module state between tests. */
export const resetBuildCheckState = () => {
    updateAvailable = false;
    reloadInProgress = false;
    subscribers.clear();
};
